import { type AuditLog, IMPORT_PROCESS_NAME_PREFIX, isNotDefined, SECOND } from '@terros/common'

export type AccountHistorySource = 'api' | 'jobNimbus' | 'bulk'

const SOURCE_BY_PROCESS = new Map<string, AccountHistorySource>([
  ['account-upsert-v1', 'api'],
  ['account-batch-v1', 'api'],
  ['account-bulk-v1', 'api'],
  ['account-job-nimbus-v1', 'jobNimbus'],
  ['account-update-worker-v1', 'bulk'],
  ['account-update-batch-v1', 'bulk'],
  ['account-modify-v1', 'bulk'],
])

// The audit log is stamped a few ms after the history entry within the same request. A log further away is
// either a different write or a later write that replaced this one (audit logs are pruned), so we don't guess.
const MAX_WRITE_DELAY = SECOND

/** Whether this audit log is the save that wrote history entries ending at `to` */
export const isWrittenWith = (audit: AuditLog, to: number): boolean =>
  audit.timestamp >= to && audit.timestamp - to <= MAX_WRITE_DELAY

type FindHistorySourceProps = {
  modifiedBy: AuditLog[] | undefined
  to: number
}

export const findHistorySource = ({ modifiedBy, to }: FindHistorySourceProps): AccountHistorySource | undefined => {
  const updateProcess = modifiedBy?.find((audit) => isWrittenWith(audit, to))?.updateProcess
  if (isNotDefined(updateProcess)) return
  // Process names are prefixed with the stage, e.g. `prod-account-add-v1`
  return [...SOURCE_BY_PROCESS].find(([name]) => updateProcess.endsWith(`-${name}`))?.[1]
}

/** Saves made by an area assignment run (the queue worker writes as the system user) */
export const isAreaAssignmentSave = (audit: AuditLog | undefined): boolean =>
  audit?.updateProcess?.endsWith('-account-assignment-worker-v1') ?? false

/** Saves made by an import, which can create an account or update an existing one */
export const isImportSave = (audit: AuditLog | undefined): boolean =>
  audit?.updateProcess?.startsWith(IMPORT_PROCESS_NAME_PREFIX) ?? false
